// apps/client/src/pages/reports/CustomerPortfolioPage.tsx
// CUSTOMER PORTFOLIO PAGE - Customers with multiple loans for relationship management
// RBAC: broker (own customers), office_admin (office customers), administrator/super_admin (all)

import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { resolveLoanBookSelection, getDisplayLoanBooks } from '../../utils/loanBookResolution';
import { ChevronDown, ChevronRight, ChevronUp, Users, Search } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { SkeletonReportPage } from '../../components/common/SkeletonLoader';
import { Spinner } from '../../components/common/Spinner';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { useCustomerPortfolio, CustomerRecord } from '../../hooks/useCustomerPortfolio';
import { formatCurrency } from '../../utils/formatters';

interface OutletContextType {
  userData: any;
  companyData: any;
  userVisibleOffices: any[];
  userVisibleTemplates: any[];
  loanBooks: ResolvedLoanBook[];
}

type SortField = 'customerName' | 'loanCount' | 'totalLoanValueCents' | 'lifetimeCommissionCents';
type SortDirection = 'asc' | 'desc';

const CustomerPortfolioPage: React.FC = () => {
  const { userData, companyData, userVisibleOffices, userVisibleTemplates, loanBooks = [] } = useOutletContext<OutletContextType>();
  const [selectedOffice, setSelectedOffice] = useState<string>('');
  const [selectedBroker, setSelectedBroker] = useState<string>('');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('all');

  const resolvedParams = useMemo(() =>
      resolveLoanBookSelection(selectedTemplate, loanBooks),
      [selectedTemplate, loanBooks]
  );

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [expandedCustomers, setExpandedCustomers] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('loanCount');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [insightsExpanded, setInsightsExpanded] = useState<boolean>(true);
  const [brokers, setBrokers] = useState<string[]>([]);
  const [loadingBrokers, setLoadingBrokers] = useState(true);
  const [fileTemplates, setFileTemplates] = useState<{ id: string; name: string }[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load brokers for filter dropdown
  useEffect(() => {
    if (!userData?.companyId || userData?.role === 'broker' || userData?.role === 'broker_assistant') {
      setLoadingBrokers(false);
      return;
    }

    const loadBrokers = async () => {
      try {
        setLoadingBrokers(true);
        const getDistinctBrokers = httpsCallable<
          { companyId: string; officeIds?: string[] },
          string[]
        >(functions, 'getDistinctBrokers');

        const officeIds = userData.role === 'office_admin' && userVisibleOffices?.length
          ? userVisibleOffices.map((o: any) => o.id)
          : undefined;

        const result = await getDistinctBrokers({
          companyId: userData.companyId,
          officeIds,
        });
        setBrokers(result.data || []);
      } catch (error) {
        console.error('Error loading brokers:', error);
        setBrokers([]);
      } finally {
        setLoadingBrokers(false);
      }
    };

    loadBrokers();
  }, [userData?.companyId, userData?.role, userVisibleOffices]);

  // Templates from centralized context (no per-page Firestore query — respects broker RBAC)
  useEffect(() => {
    const templates = (userVisibleTemplates || []).map((t: any) => ({
      id: t.id,
      name: t.templateName || t.name || t.id,
    }));
    setFileTemplates(templates.sort((a, b) => a.name.localeCompare(b.name)));
  }, [userVisibleTemplates]);

  // Debounce search input (500ms - gives users time to finish typing)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch customer portfolio data (discharged loans are excluded)
  const { data, loading, error, refetch } = useCustomerPortfolio({
    companyId: userData?.companyId || '',
    officeId: selectedOffice || undefined,
    brokerName: selectedBroker || undefined,
    search: debouncedSearch || undefined,
    templateId: resolvedParams.templateId,
    entityCode: resolvedParams.entityCode,
  });

  // Track initial load vs subsequent filter changes
  useEffect(() => {
    if (!loading && data !== null) {
      setIsInitialLoad(false);
    }
  }, [loading, data]);

  // Sort customers based on current sort state
  const sortedCustomers = useMemo(() => {
    if (!data?.customers) return [];

    return [...data.customers].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'customerName':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        case 'loanCount':
          comparison = a.loanCount - b.loanCount;
          break;
        case 'totalLoanValueCents':
          comparison = a.totalLoanValueCents - b.totalLoanValueCents;
          break;
        case 'lifetimeCommissionCents':
          comparison = a.lifetimeCommissionCents - b.lifetimeCommissionCents;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data?.customers, sortField, sortDirection]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // New field, default to descending (most useful for numbers)
      setSortField(field);
      setSortDirection(field === 'customerName' ? 'asc' : 'desc');
    }
  };

  // Render sort indicator
  const SortIndicator: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <span className="ml-1 text-gray-600">↕</span>;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp size={14} className="ml-1 inline" />
    ) : (
      <ChevronDown size={14} className="ml-1 inline" />
    );
  };

  // Helper to resolve office name from office ID
  const getOfficeName = (officeId: string | null): string => {
    if (!officeId) return '—';
    const office = userVisibleOffices?.find((o) => o.id === officeId);
    return office?.name || officeId;  // Fallback to ID if name not found
  };

  // Toggle expanded state for customer loan details
  const toggleCustomerExpanded = (customerName: string) => {
    setExpandedCustomers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(customerName)) {
        newSet.delete(customerName);
      } else {
        newSet.add(customerName);
      }
      return newSet;
    });
  };

  // Render loading state - only show full-page spinner on initial load
  // For subsequent searches/filters, keep the UI visible with inline indicator
  if (loading && isInitialLoad) {
    return <SkeletonReportPage statCards={5} statColumns={4} tableColumns={5} />;
  }

  // Render error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="bg-red-900/50 border border-red-500 text-red-200 p-8 rounded-lg max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4">Error Loading Data</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Users}
        title="Customer Portfolio"
        subtitle="High-value customers with multiple active loans (excludes discharged)"
        onRefresh={() => refetch()}
        isRefreshing={loading}
      />

      {/* Summary Stats */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8 px-8">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Multi-Loan Customers</div>
            <div className="text-3xl font-bold">{data.summary.totalCustomers}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Active Loans</div>
            <div className="text-3xl font-bold">{data.summary.totalLoans}</div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Avg Portfolio Age</div>
            <div className="text-3xl font-bold">
              {data.summary.averageAgeYears?.toFixed(1) || '0.0'} yrs
            </div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Upfront</div>
            <div className="text-3xl font-bold">
              {formatCurrency(data.summary.totalUpfrontCommissionCents)}
            </div>
          </div>
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-gray-400 text-sm mb-1">Total Trail</div>
            <div className="text-3xl font-bold">
              {formatCurrency(data.summary.totalTrailCommissionCents)}
            </div>
          </div>
        </div>
      )}

        {/* Insights Panel - Collapsible accordion with key metrics */}
        {data && data.customers.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border border-purple-500/30 rounded-xl overflow-hidden">
            <button
              onClick={() => setInsightsExpanded(!insightsExpanded)}
              className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            >
              <h3 className="text-lg font-bold text-white">Insights</h3>
              {insightsExpanded ? (
                <ChevronUp size={20} className="text-purple-300" />
              ) : (
                <ChevronDown size={20} className="text-purple-300" />
              )}
            </button>
            {insightsExpanded && (
              <div className="px-6 pb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-purple-300 font-medium mb-1">Portfolio Concentration</div>
                    <div className="text-white">
                      {((data.summary.totalCustomers / (data.summary.totalLoans || 1)) * 100).toFixed(1)}% of customers
                      have multiple loans
                    </div>
                  </div>
                  <div>
                    <div className="text-purple-300 font-medium mb-1">Average Loans per Customer</div>
                    <div className="text-white">
                      {(data.summary.totalLoans / data.summary.totalCustomers).toFixed(2)} loans
                    </div>
                  </div>
                  <div>
                    <div className="text-purple-300 font-medium mb-1">Avg Commission per Customer</div>
                    <div className="text-white">
                      {formatCurrency(data.summary.totalCommissionCents / data.summary.totalCustomers)}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-purple-200 text-xs">
                  <strong>Tip:</strong> Customers with multiple loans are high-value relationships. Consider targeted
                  outreach for cross-sell opportunities (insurance, investment loans) and referral programs.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loan Book Tabs */}
        {loanBooks.length > 0 && (
          <div className="mb-6 border-b border-gray-700">
            <nav className="flex space-x-6">
              <button
                onClick={() => setSelectedTemplate('all')}
                className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  selectedTemplate === 'all'
                    ? 'text-purple-500 border-purple-500'
                    : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
                }`}
              >
                All Loan Books
              </button>
              {getDisplayLoanBooks(loanBooks).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedTemplate(opt.value)}
                  className={`pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                    selectedTemplate === opt.value
                      ? 'text-purple-500 border-purple-500'
                      : 'text-gray-400 border-transparent hover:text-white hover:border-gray-500'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 bg-gray-800 p-6 rounded-xl shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search Bar */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Search Customer
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search by customer name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              </div>
            </div>

            {/* Office Filter (for non-brokers with multiple offices) */}
            {userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && userVisibleOffices && userVisibleOffices.length > 1 && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Filter by Office
                </label>
                <select
                  value={selectedOffice}
                  onChange={(e) => setSelectedOffice(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">All Offices</option>
                  {userVisibleOffices.map((office: any) => (
                    <option key={office.id} value={office.id}>
                      {office.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Broker Filter (for non-brokers) */}
            {userData?.role !== 'broker' && userData?.role !== 'broker_assistant' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Filter by Broker
                </label>
                <select
                  value={selectedBroker}
                  onChange={(e) => setSelectedBroker(e.target.value)}
                  disabled={loadingBrokers}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  <option value="">All Brokers</option>
                  {brokers.map((broker) => (
                    <option key={broker} value={broker}>
                      {broker}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Active Filters Summary */}
          {(searchQuery || selectedOffice || selectedBroker || selectedTemplate !== 'all') && (
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-400">Active filters:</span>
              {selectedTemplate !== 'all' && (
                <span className="px-2 py-1 bg-purple-600/50 rounded text-xs text-purple-200">
                  Loan Book: {fileTemplates.find(t => t.id === selectedTemplate)?.name}
                </span>
              )}
              {searchQuery && (
                <span className="px-2 py-1 bg-purple-600/50 rounded text-xs text-purple-200">
                  Search: "{searchQuery}"
                </span>
              )}
              {selectedOffice && (
                <span className="px-2 py-1 bg-purple-600/50 rounded text-xs text-purple-200">
                  Office: {userVisibleOffices?.find((o: any) => o.id === selectedOffice)?.name}
                </span>
              )}
              {selectedBroker && (
                <span className="px-2 py-1 bg-purple-600/50 rounded text-xs text-purple-200">
                  Broker: {selectedBroker}
                </span>
              )}
              <button
                onClick={() => {
                  setSelectedTemplate('all');
                  setSearchQuery('');
                  setSelectedOffice('');
                  setSelectedBroker('');
                }}
                className="ml-2 text-sm text-purple-400 hover:text-purple-300"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Customer Portfolio Table */}
        <div className="bg-gray-800 rounded-xl shadow-lg overflow-hidden relative">
          {/* Inline loading overlay for subsequent searches */}
          {loading && !isInitialLoad && (
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm z-10 flex items-center justify-center">
              <div className="flex items-center gap-3 bg-gray-800 px-4 py-2 rounded-lg">
                <Spinner size="sm" />
                <span className="text-gray-300 text-sm">Searching...</span>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-900">
                <tr>
                  <th
                    className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => handleSort('customerName')}
                  >
                    Customer Name
                    <SortIndicator field="customerName" />
                  </th>
                  <th
                    className="px-6 py-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => handleSort('loanCount')}
                  >
                    Loan Count
                    <SortIndicator field="loanCount" />
                  </th>
                  <th
                    className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => handleSort('totalLoanValueCents')}
                  >
                    Total Loan Value
                    <SortIndicator field="totalLoanValueCents" />
                  </th>
                  <th
                    className="px-6 py-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:text-white transition-colors select-none"
                    onClick={() => handleSort('lifetimeCommissionCents')}
                  >
                    <div>
                      Lifetime Commission
                      <SortIndicator field="lifetimeCommissionCents" />
                    </div>
                    <div className="text-[10px] font-normal normal-case mt-0.5">
                      <span className="text-blue-400">Upfront</span>
                      {' / '}
                      <span className="text-purple-400">Trail</span>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Details
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sortedCustomers.length > 0 ? (
                  sortedCustomers.map((customer: CustomerRecord) => {
                    const isExpanded = expandedCustomers.has(customer.customerName);
                    return (
                      <React.Fragment key={customer.customerName}>
                        <tr className="hover:bg-gray-700 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                            {customer.customerName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-600 text-white">
                              {customer.loanCount} loans
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-300">
                            {formatCurrency(customer.totalLoanValueCents)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                            <div className="text-green-400 font-medium">
                              {formatCurrency(customer.lifetimeCommissionCents)}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              <span className="text-blue-400">{formatCurrency(customer.upfrontCommissionCents)}</span>
                              {' / '}
                              <span className="text-purple-400">{formatCurrency(customer.trailCommissionCents)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                            <button
                              onClick={() => toggleCustomerExpanded(customer.customerName)}
                              className="inline-flex items-center gap-2 px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronDown size={16} />
                                  Hide Loans
                                </>
                              ) : (
                                <>
                                  <ChevronRight size={16} />
                                  Show Loans
                                </>
                              )}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="px-6 py-4 bg-gray-900/50">
                              <div className="space-y-3">
                                <div className="text-sm font-medium text-gray-400 mb-3">
                                  Active Loans ({customer.loanCount} total
                                  {customer.loans?.length >= 20 ? ', showing first 20' : ''}):
                                </div>
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                  {(customer.loans || []).map((loan) => (
                                    <div
                                      key={loan.loanId}
                                      className="bg-gray-800 border border-gray-700 rounded-lg p-4"
                                    >
                                      <div className="flex justify-between items-start mb-3">
                                        <span className="text-sm text-white font-mono font-medium">
                                          Loan ID: {loan.loanId}
                                        </span>
                                        <span className="text-sm text-green-400 font-semibold">
                                          {loan.loanAmountCents > 0
                                            ? formatCurrency(loan.loanAmountCents)
                                            : '—'}
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2 text-xs">
                                        <div>
                                          <span className="text-gray-500">Broker:</span>
                                          <span className="ml-1 text-gray-300">
                                            {loan.brokerName || '—'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Office:</span>
                                          <span className="ml-1 text-gray-300">
                                            {getOfficeName(loan.officeId)}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Lender:</span>
                                          <span className="ml-1 text-gray-300">
                                            {loan.lender || '—'}
                                          </span>
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Settled:</span>
                                          <span className="ml-1 text-gray-300">
                                            {loan.settlementDate
                                              ? new Date(loan.settlementDate).toLocaleDateString(
                                                  'en-AU',
                                                  { day: '2-digit', month: 'short', year: 'numeric' }
                                                )
                                              : '—'}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="text-gray-400">
                        <Users size={48} className="mx-auto mb-4 opacity-50" />
                        <p className="text-lg font-medium">No customers with multiple loans found</p>
                        <p className="text-sm mt-2">
                          Try adjusting your filters or check back after more data is uploaded.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

    </div>
  );
};

export default CustomerPortfolioPage;
